/**
 * Quais apólices entram no aviso da tela inicial.
 *
 * Papel: aqui fica `apurarVencimentosDePlanos`, que mapeia cada apólice para o
 * formato do aviso, com o lembrete de atualizar a foto da carteirinha, e
 * entrega a `apurarVencimentos`, de `shared/lib/vencimento.ts`, com a janela
 * `JANELA_DA_CARTEIRINHA_EM_DIAS`.
 *
 * Como usar: a carteirinha do plano renova sozinha a cada ano, e por isso a
 * apólice não é anunciada com antecedência, como os documentos: ela só entra
 * no aviso a partir do dia seguinte ao da validade, como item vencido, e a
 * linha pede a foto nova da carteirinha — "Atualizar foto da carteirinha
 * (validade anterior: DD/MM/AAAA)". Passar a apólice pela
 * `JANELA_DE_VENCIMENTO_EM_DIAS` dos documentos faria o aviso anunciar, por
 * seis meses de cada ano, um vencimento que se resolve sem ninguém fazer nada.
 *
 * Este arquivo sabe apenas o que é de plano de saúde — o rótulo que a linha
 * leva no aviso, a pessoa escrita por `formatarPessoa`, a mesma forma da
 * `TabelaPlanos`, e a rota onde a linha se resolve, `/saude`, onde se troca a
 * foto. A contagem de dias e o descarte de quem não tem validade são de
 * `shared/`.
 *
 * O rótulo traz o nome do plano, e não só a palavra "plano": uma pessoa tem
 * medicina e odontologia vencendo no mesmo dia, e duas linhas idênticas no
 * aviso não diriam qual das duas resolver.
 */
import { type PlanoSaude } from '@concierge/shared';

import { formatarPessoa } from '@/shared/lib/formato';
import { type ItemVencendo, apurarVencimentos } from '@/shared/lib/vencimento';

/**
 * A janela das apólices: negativa, para que só entre a validade que já passou.
 *
 * Com `-1`, a apólice que vence hoje ainda não aparece, e a que venceu ontem
 * aparece. Trocar por zero anunciaria a carteirinha no próprio dia da
 * validade, quando ela ainda vale.
 */
const JANELA_DA_CARTEIRINHA_EM_DIAS = -1;

/** O que a linha da apólice vencida pede, no lugar do prazo. */
const LEMBRETE_DA_CARTEIRINHA = 'Atualizar foto da carteirinha';

/**
 * Apura as apólices que entram no aviso: só as de validade já vencida.
 *
 * @param planos - As apólices como a API as devolve.
 * @param hojeIso - A data de hoje em `AAAA-MM-DD`, de quem chama.
 * @returns Os itens a anunciar, na ordem em que vieram; quem ordena é o
 *   `PopupVencimentos`.
 */
export function apurarVencimentosDePlanos(
  planos: readonly PlanoSaude[],
  hojeIso: string,
): ItemVencendo[] {
  return apurarVencimentos(
    planos.map((apolice) => ({
      id: apolice.id,
      referente: formatarPessoa(apolice.pessoa),
      rotulo: `Plano de saúde — ${apolice.plano}`,
      validade: apolice.validade,
      destino: '/saude',
      lembrete: LEMBRETE_DA_CARTEIRINHA,
    })),
    hojeIso,
    JANELA_DA_CARTEIRINHA_EM_DIAS,
  );
}
